import React, { ReactNode, ReactElement, useState } from 'react';
import SVG from "react-inlinesvg";
import { Modal } from "react-bootstrap";
import { Button } from "react-bootstrap";
import { toAbsoluteUrl } from "../../_metronic/_helpers";
import { Field, Form, Formik } from 'formik';

interface ImportFromFileProps {
    formTitle?: string,
    buttonTitle?: string,
    validateButtonTitle?: string,
    enableDryRun?: boolean,
    closeOnDone?: boolean,
    className?: string,
    onFileSelected?: (file: File | null) => void,
    onImport: (file: File, dryRun: boolean) => Promise<{ success: boolean, messages: string[] }>,
    onReset?: () => void,
    children: ReactNode,
}

const ImportFromFile = ({ formTitle, buttonTitle, validateButtonTitle,  enableDryRun, closeOnDone, className, onFileSelected, onImport, onReset, children }: ImportFromFileProps) => {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isDryRun, setDryRun] = useState(true);
  const [messages, setMessages] = useState([] as string[]);

  const childrenArray = React.Children.toArray(children) as ReactElement[];

  const importButton = childrenArray.find(child => child.type === ImportFromFile.ImportButton);
  const popupContent = childrenArray.find(child => child.type === ImportFromFile.PopupContent);

  const showImportForm = () =>{
    setDialogOpen(true);
    setMessages([]);
    if (onReset) {
      onReset();
    }
  };

  return (
    <>
      <button onClick={showImportForm} className={className || "d-flex align-items-center btn-sm"}>
        {importButton ?? <><div className="h1 text-primary mt-2">
          <SVG src={toAbsoluteUrl("/media/svg/icons/Files/Import.svg")} className="svg-icon-md" />
        </div>
        <span className="ml-2">Import</span></> }
      </button>
      <Formik
        initialValues={{
          fileToUpload: undefined as File | undefined
        }}
        onSubmit={async (values, { setStatus, setFieldValue }) => {
          try {
            if (values.fileToUpload !== undefined) {
              var result = await onImport(values.fileToUpload, isDryRun);
              setMessages(result.messages);
              if (result.success) {
                setStatus("success");
                if (!isDryRun) {
                  values.fileToUpload = undefined;
                }
              } else {
                  setStatus("error");
              }
              if (closeOnDone && !isDryRun) {
                setDialogOpen(false);
              }
            }
          } catch (error) {
              setStatus("error");
          }
        }}
      >
        {({ handleSubmit, handleChange, setFieldValue, status, values }) => (
          <Modal size="lg" show={isDialogOpen} aria-labelledby="example-modal-sizes-title-lg" key="Test">
            <Modal.Header>
                <Modal.Title id="example-modal-sizes-title-lg">
                {formTitle || "Import"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="overlay overlay-block cursor-default">
              <Form className="form form-label-right">
                <div className="form-group">
                  <label htmlFor="file" className="sr-only">
                    Choose a file
                  </label>
                  <Field name="fileToUpload">
                    {({ field }: any) => (
                    <input
                      type="file"
                      className="btn btn-primary btn-elevate"
                      onChange={async (event) => {
                        setFieldValue(field.name, event.currentTarget.files && event.currentTarget.files[0]);
                        if (onFileSelected) {
                          await onFileSelected(event.currentTarget.files && event.currentTarget.files[0]);
                        }
                      }}
                    />
                    )}
                  </Field>
                  {values.fileToUpload && (
                  <section>
                      <h4>File details:</h4>
                      <ul>
                      <li>Name: {values.fileToUpload.name}</li>
                      <li>Type: {values.fileToUpload.type}</li>
                      <li>Size: {values.fileToUpload.size} bytes</li>
                      </ul>
                  </section>
                  )}
                </div>
                {popupContent}
              </Form>
            </Modal.Body>
            <Modal.Footer>
              {enableDryRun && 
                <button
                  type="submit"
                  disabled={!values.fileToUpload}
                  onClick={() => { setDryRun(true); handleSubmit(); }}
                  className="btn btn-primary btn-elevate"
                >
                  {validateButtonTitle || "Validate"}
                </button>
              }
              <button
                type="submit"
                disabled={!values.fileToUpload}
                onClick={() => { setDryRun(false); handleSubmit(); }}
                className="btn btn-primary btn-elevate"
              >
                Import
              </button>
              <button
                onClick={() => { setDialogOpen(false); }}
                className="btn btn-primary btn-elevate"
              >
                Close
              </button>
            </Modal.Footer>
            {status === "success" && (
            <div className="text-success text-center mt-3 p-10">
              <div>{isDryRun ? "Valid" : closeOnDone ? "" : "Imported successfully!"}</div>
              {messages.map((m, index) => (
                <div key={index}>{m}</div>
              ))}
              {isDryRun && (
                <div>
                  Data were not imported into the system yet.
                </div>
              )}
            </div>
            )}
            {status === "error" && (
            <div className="text-danger text-center mt-3 p-10">
              An error occurred.
              {messages.map((m, index) => (
                <div key={index}>{m}</div>
              ))}
            </div>
            )}
          </Modal>
          )}
        </Formik>
    </>
  );
};

ImportFromFile.ImportButton = ({ children }: { children: ReactNode }) => <>{children}</>;
ImportFromFile.PopupContent = ({ children }: { children: ReactNode }) => <>{children}</>;

export default ImportFromFile;